import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DistributionManager } from '../APIManager'
import { type IPagination } from '../models/DataObject'
import { type StudentDistribution } from '../models/Distribution'
import { getSurveyState } from '../features/StudentSurveyListFeature'
import { SurveyCompletionState } from '../features/StudentSurveyListFeature/components/SurveyListCard/styles'
import { isStudentSurveyReopenEnabled } from '../utils/tenantFeatureFlags'

/**
 * The student survey list, shared by every surface that reads it (CR-293).
 *
 * Two consumers, which is why this is a hook rather than page-local state:
 *  - `StudentDashboardPage` — the card list, on whichever page the student is viewing
 *  - `CustomAppbar/TabMenu` — gates the "Take Surveys" nav button
 *
 * `CustomAppbar/AccountDropdown` is deliberately *not* a consumer: its due-surveys badge is
 * commented out, and it was reading the list only to compute a count nobody renders. If that badge
 * is ever restored, it should read `actionableCount` from here — see the note in that file.
 *
 * They share one React Query cache entry per page number, so the nav bar and the dashboard do not
 * issue separate requests for the same page. The nav surfaces always ask for page 1: they only
 * need `actionableCount`, which is whole-result-set data present on every page, and page 1 is
 * already fetched on first load. A student paging deeper causes one additional request for that
 * page, which is correct rather than wasteful.
 *
 * Spec: docs/specs/active/2026-08-21-student-dashboard-pagination/spec.md
 */
export const STUDENT_DISTRIBUTIONS_KEY = 'studentDistributions'

/**
 * Cards per page. Matches the instructor dashboard deliberately — the whole reason this surface
 * was migrated is to have one paging pattern across the three dashboards.
 */
export const STUDENT_CARDS_PAGE_SIZE = 20

/**
 * Whether to offer a student a route to their surveys, given the server's whole-list count.
 *
 * Lives here rather than at the call site because the `undefined` rule is the load-bearing part
 * and there must be exactly one of it: `undefined` means *not known* — a paginated response from a
 * backend that has not shipped the count — and it must never collapse into zero. Showing a route
 * that turns out to be empty is a far smaller failure than hiding the only route to surveys a
 * student does have.
 */
export const hasActionableSurveys = (actionableCount?: number): boolean =>
  actionableCount == null ? true : actionableCount > 0

export interface UseStudentDistributionsResult {
  surveys: StudentDistribution[]
  /** This page's pagination. Absent on error — React Query drops `data`. */
  pagination?: IPagination
  /**
   * The last pagination read successfully for this list, which survives a failed page so the
   * pager stays reachable instead of stranding the student on the page that failed.
   */
  lastPagination?: IPagination
  /**
   * How many surveys the student can still act on, across the whole list — never just this page.
   * `undefined` means "not known", which is different from zero and must be treated as such by
   * anything that hides UI: see the nav consumers.
   */
  actionableCount?: number
  totalResults: number
  isLoading: boolean
  isError: boolean
  error: unknown
}

/**
 * Fallback for a bare-array response, i.e. a backend that has not shipped the CDG envelope yet.
 *
 * Deriving the count client-side is exactly the bug this work removes — but it is sound *only*
 * here, because a bare array is the complete list rather than a page. Guarded on the absence of a
 * pagination block so it can never run against a page. Delete this once the envelope is live
 * everywhere; until then it is what makes this change safe to ship before the backend.
 */
/**
 * The states a card renders with a working button the user can press.
 *
 * Derived through `getSurveyState` rather than re-implemented, because the two must not disagree:
 * this count decides whether the nav button that *reaches* these cards is rendered at all, so a
 * predicate that is stricter than the cards' own hides surveys the user could act on. The version
 * this replaced tested `!isSubmitted`, which missed `ReopenSurvey` — a submitted survey stays
 * actionable until its end date wherever reopen is enabled (CE-193, Pitt). A user whose surveys
 * were all submitted-but-reopenable counted zero and lost the button.
 */
const ACTIONABLE_STATES: ReadonlySet<SurveyCompletionState> = new Set([
  SurveyCompletionState.StartSurvey,
  SurveyCompletionState.ReopenSurvey
])

const countActionableInFullList = (surveys: StudentDistribution[]): number => {
  const reopenEnabled = isStudentSurveyReopenEnabled()
  return surveys.filter(survey =>
    ACTIONABLE_STATES.has(getSurveyState(survey.isSubmitted, survey.endTimestamp, reopenEnabled))
  ).length
}

export const useStudentDistributions = (
  pageNumber = 1,
  enabled = true
): UseStudentDistributionsResult => {
  // Carried across a failed page so the pager stays reachable. Assigning during render is safe: it
  // is a pure carry-forward of a derived value, not a state update. No scope key, unlike the
  // instructor and admin dashboards: there is exactly one list per student, with no term or subject
  // to switch between, and `queryClient.clear()` on logout drops it before another user can see it.
  //
  // Per *instance*, not shared: a `useRef` in a hook gives each caller its own, so `TabMenu` and
  // `StudentDashboardPage` each carry their own copy. That is fine while only the dashboard reads
  // `lastPagination`, but a second consumer needing the same carried value would need this lifted
  // to a provider rather than assuming this line already shares it.
  const lastPaginationRef = useRef<IPagination | undefined>(undefined)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [STUDENT_DISTRIBUTIONS_KEY, pageNumber],
    queryFn: async () => await DistributionManager.getStudentDistributions({
      pageNumber,
      pageSize: STUDENT_CARDS_PAGE_SIZE
    }),
    enabled,
    retry: false,
    // The global query-cache subscriber in ReactQueryProvider toasts every non-silent query
    // error with no domain wording, and it does not de-duplicate against a caller that also
    // notifies — so a page that wants its own message has to opt out of the generic one or
    // show two. `StudentDashboardPage` owns the message (with the wording the deleted Redux
    // slice used); when only the nav bar has this query mounted, a failed background fetch
    // stays quiet and the nav gate fails open, which is the right outcome for a fetch the
    // student did not ask for.
    meta: { silent: true }
  })

  const surveys = (data?.data ?? []) as StudentDistribution[]
  const pagination = data?.pagination
  if (pagination != null) lastPaginationRef.current = pagination

  return {
    surveys,
    pagination,
    lastPagination: pagination ?? lastPaginationRef.current,
    actionableCount: data?.actionableCount ?? (
      // No envelope at all → the array is everything, so counting it is correct.
      // Envelope but no count → genuinely unknown; stay undefined rather than report a page total
      // as if it were the whole list.
      data != null && pagination == null ? countActionableInFullList(surveys) : undefined
    ),
    totalResults: pagination?.totalResults ?? surveys.length,
    isLoading,
    isError,
    error
  }
}
